import React, { memo, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import FormControl from '@material-ui/core/FormControl'
import classNames from 'classnames'
import Input from '@material-ui/core/Input'
import InputAdornment from '@material-ui/core/InputAdornment'
import Visibility from '@material-ui/icons/Visibility'
import VisibilityOff from '@material-ui/icons/VisibilityOff'
import EmailIcon from '@material-ui/icons/AlternateEmailSharp'
import PasswordIcon from '@material-ui/icons/LockOutlined'
import EditIcon from '@material-ui/icons/EditOutlined'
import IconButton from '@material-ui/core/IconButton'
import DoneIcon from '@material-ui/icons/DoneRounded'
import ShortText from '@material-ui/icons/ShortText'

import { useAuthState, AuthStore } from '../../reducer'
import styles from './styles'
import {
	EAuthTextfieldType,
	IAuthTextfieldProps,
	EAuthActionTypes,
} from 'models'

const useStyles = makeStyles(styles)
const icons = {
	[EAuthTextfieldType.email]: EmailIcon,
	[EAuthTextfieldType.password]: PasswordIcon,
	[EAuthTextfieldType.firstName]: null,
	[EAuthTextfieldType.lastName]: null,
}
const actions = {
	[EAuthTextfieldType.email]: EAuthActionTypes.setEmail,
	[EAuthTextfieldType.password]: EAuthActionTypes.setPassword,
	[EAuthTextfieldType.firstName]: EAuthActionTypes.setFirstName,
	[EAuthTextfieldType.lastName]: EAuthActionTypes.setLastName,
}

const TextFieldView: React.FunctionComponent<IAuthTextfieldProps> = memo(({
	type,
	placeholder,
	disabled,
	style,
	values,
}) => {
	const classes = useStyles()
	const [showPassword, setShowPassword] = useState(false)
	const [capslock, setCapslock] = useState(false)
	const [, dispatch] = useAuthState()
	const Icon = icons[type]
	let endAdornment = null

	const previousStep = () => {
		dispatch({
			type: EAuthActionTypes.decrementStep,
		})
	}
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		dispatch({
			type: actions[type],
			[type]: event.target.value,
		})
	}
	const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		const currentCapsLock = event.getModifierState('CapsLock')
		if (currentCapsLock !== capslock) setCapslock(currentCapsLock)
	}
	if (type === EAuthTextfieldType.password) {
		const toggleShowPassword = () => setShowPassword(!showPassword)
		endAdornment = (
			<InputAdornment position="end">
				<IconButton
					role="presentation"
					aria-hidden={true}
					aria-label="Toggle password visibility"
					onClick={toggleShowPassword}
				>
					{showPassword ? (
						<VisibilityOff
							className={classes.showPasswordIconOff}
						/>
					) : (
							<Visibility className={classes.showPasswordIconOn} />
						)}
				</IconButton>
			</InputAdornment>
		)
	} else {
		endAdornment = (
			<InputAdornment position="end">
				<IconButton
					disabled={disabled ? !disabled : true}
					role="presentation"
					aria-hidden={true}
					onClick={
						(type === EAuthTextfieldType.email &&
							disabled &&
							previousStep) ||
						undefined
					}
				>
					{type === EAuthTextfieldType.email && disabled ? (
						<EditIcon className={classes.editIcon} />
					) : true ? (
						<ShortText className={classes.shortTextIcon} />
					) : (
								<DoneIcon className={classes.validIcon} />
							)}
				</IconButton>
			</InputAdornment>
		)
	}

	return (
		<FormControl
			className={classNames(classes.margin, classes.container)}
			style={style || {}}
		>
			<Input
				onKeyUp={onKeyDown}
				placeholder={
					type === EAuthTextfieldType.email
						? 'Email address'
						: placeholder || 'Password'
				}
				type={
					type === EAuthTextfieldType.password
						? showPassword
							? 'text'
							: EAuthTextfieldType.password
						: EAuthTextfieldType[type]
				}
				value={values[type]}
				onChange={handleChange}
				classes={{
					input: classes.textfield,
					root: classes.textfieldRoot,
				}}
				startAdornment={
					type && Icon ? (
						<InputAdornment position="start">
							<Icon className={classes.adornment} />
						</InputAdornment>
					) : null
				}
				endAdornment={endAdornment}
				disabled={disabled || false}
			/>
		</FormControl>
	)
})

export default (props: any) => <AuthStore keys={['values']}><TextFieldView {...props} /></AuthStore>